import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { LogOut, RefreshCw, Phone, MapPin, Clock, Check, X, ChefHat, PackageCheck, Package, Loader2, Volume2, VolumeX, Bell, Sparkles, BarChart3 } from "lucide-react";

// POS-style status flow per user requirement:
// pending → accepted → preparing → ready → completed
// Allow rejected from pending only.
const POS_STATUS = {
    pending:    { label: "ΝΕΑ",         color: "#f4b339", bg: "rgba(244,179,57,0.15)",  actions: ["accept", "reject"] },
    accepted:   { label: "ΑΠΟΔΕΚΤΗ",    color: "#39ff8f", bg: "rgba(57,255,143,0.12)",  actions: ["preparing"] },
    preparing:  { label: "ΕΤΟΙΜΑΖΕΤΑΙ", color: "#ff9f1c", bg: "rgba(255,159,28,0.15)",  actions: ["ready"] },
    ready:      { label: "ΕΤΟΙΜΗ",      color: "#00f0ff", bg: "rgba(0,240,255,0.15)",   actions: ["completed"] },
    on_the_way: { label: "ΕΤΟΙΜΗ",      color: "#00f0ff", bg: "rgba(0,240,255,0.15)",   actions: ["completed"] },
    completed:  { label: "ΟΛΟΚΛΗΡΩΘΗΚΕ", color: "#39ff8f", bg: "rgba(57,255,143,0.10)", actions: [] },
    delivered:  { label: "ΟΛΟΚΛΗΡΩΘΗΚΕ", color: "#39ff8f", bg: "rgba(57,255,143,0.10)", actions: [] },
    rejected:   { label: "ΑΠΟΡΡΙΦΘΗΚΕ",  color: "#ff2d4f", bg: "rgba(255,45,79,0.10)",  actions: [] },
    cancelled:  { label: "ΑΚΥΡΩΘΗΚΕ",    color: "#888",    bg: "rgba(136,136,136,0.10)", actions: [] },
};

const ACTIONS = {
    accept:    { label: "ΔΕΧΟΜΑΙ",     icon: Check,         color: "#39ff8f", to: "accepted" },
    reject:    { label: "ΑΠΟΡΡΙΨΗ",    icon: X,             color: "#ff2d4f", to: "rejected" },
    preparing: { label: "ΕΤΟΙΜΑΣΤΗΚΕ", icon: ChefHat,       color: "#ff9f1c", to: "preparing" },
    ready:     { label: "ΕΤΟΙΜΗ",      icon: Package,       color: "#00f0ff", to: "ready" },
    completed: { label: "ΠΑΡΑΔΟΘΗΚΕ",  icon: PackageCheck,  color: "#39ff8f", to: "completed" },
};

const playOrderSound = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const note = (freq, start, dur) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
            gain.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
            osc.connect(gain).connect(ctx.destination);
            osc.start(ctx.currentTime + start);
            osc.stop(ctx.currentTime + start + dur);
        };
        note(880, 0, 0.18);
        note(1320, 0.18, 0.22);
        note(1760, 0.4, 0.25);
    } catch {}
};

export const PartnerDashboard = () => {
    const navigate = useNavigate();
    const { user, login, logout } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loginLoading, setLoginLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [storeData, setStoreData] = useState(null);
    const [filter, setFilter] = useState("active");
    const [refreshing, setRefreshing] = useState(false);
    const [soundOn, setSoundOn] = useState(() => localStorage.getItem("partner_sound") !== "off");
    const [highlightId, setHighlightId] = useState(null);
    const wsRef = useRef(null);

    const isStoreOwner = user?.role === "store_owner" || user?.role === "admin";

    const fetchData = useCallback(async () => {
        if (!isStoreOwner) return;
        setRefreshing(true);
        try {
            const [meRes, ordersRes] = await Promise.all([
                api.get("/store/me"),
                api.get("/store/orders"),
            ]);
            setStoreData(meRes.data.store);
            setOrders(ordersRes.data || []);
        } catch (err) {
            toast.error("Σφάλμα φόρτωσης");
        } finally {
            setRefreshing(false);
        }
    }, [isStoreOwner]);

    useEffect(() => { if (isStoreOwner) fetchData(); }, [isStoreOwner, fetchData]);

    // Polling fallback
    useEffect(() => {
        if (!isStoreOwner) return;
        const id = setInterval(fetchData, 20000);
        return () => clearInterval(id);
    }, [isStoreOwner, fetchData]);

    // WebSocket real-time
    useEffect(() => {
        if (!isStoreOwner) return;
        const token = localStorage.getItem("sh_token");
        if (!token) return;
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
        const ws = new WebSocket(`${base}/api/ws/store?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
            try {
                const msg = JSON.parse(ev.data);
                if (msg.type === "new_order") {
                    setOrders((prev) => [msg.order, ...prev.filter((o) => o.id !== msg.order.id)]);
                    setHighlightId(msg.order.id);
                    setTimeout(() => setHighlightId(null), 6000);
                    if (soundOn) playOrderSound();
                    toast.success(`🔔 Νέα παραγγελία — ${msg.order.customer_name}`, { duration: 5000 });
                    if ("Notification" in window && Notification.permission === "granted") {
                        new Notification("Νέα παραγγελία", { body: `${msg.order.customer_name} — €${msg.order.total.toFixed(2)}` });
                    }
                } else if (msg.type === "order_updated") {
                    setOrders((prev) => prev.map((o) => (o.id === msg.order.id ? msg.order : o)));
                }
            } catch {}
        };
        return () => { try { ws.close(); } catch {} };
    }, [isStoreOwner, soundOn]);

    useEffect(() => {
        if (isStoreOwner && "Notification" in window && Notification.permission === "default") {
            Notification.requestPermission();
        }
    }, [isStoreOwner]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginLoading(true);
        try {
            const u = await login(email.trim(), password);
            if (u.role !== "store_owner" && u.role !== "admin") {
                toast.error("Μόνο store accounts μπορούν να συνδεθούν εδώ");
                await logout();
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Σφάλμα σύνδεσης");
        } finally { setLoginLoading(false); }
    };

    const updateStatus = async (orderId, newStatus) => {
        try {
            await api.patch(`/store/orders/${orderId}/status`, { status: newStatus });
            const labels = { accepted: "Δεκτή!", preparing: "Σε ετοιμασία", ready: "Έτοιμη", completed: "Ολοκληρώθηκε", rejected: "Απορρίφθηκε" };
            toast.success(labels[newStatus] || newStatus);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Σφάλμα");
        }
    };

    const toggleSound = () => {
        const next = !soundOn;
        setSoundOn(next);
        localStorage.setItem("partner_sound", next ? "on" : "off");
        if (next) playOrderSound();
    };

    if (!user || !isStoreOwner) {
        return (
            <div className="cyber-bg cyber-grid scanlines min-h-screen flex items-center justify-center p-6 text-white">
                <div className="w-full max-w-sm" data-testid="partner-login">
                    <div className="text-center mb-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--gold-light)]">// partner_portal</p>
                        <h1 className="font-heading text-4xl mt-2" style={{ fontFamily: '"Cormorant Garamond", serif' }}>Partner Login</h1>
                        <p className="text-xs text-white/55 mt-2">Δεδικασμένο portal διαχείρισης παραγγελιών.</p>
                    </div>
                    <form onSubmit={handleLogin} className="space-y-3">
                        <input type="email" required data-testid="partner-email" placeholder="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            className="w-full glass-gold px-5 h-14 text-base rounded-2xl focus:outline-none focus:border-[var(--gold)] placeholder:text-white/40 border-2 border-white/15" />
                        <input type="password" required data-testid="partner-password" placeholder="password" value={password} onChange={(e) => setPassword(e.target.value)}
                            className="w-full glass-gold px-5 h-14 text-base rounded-2xl focus:outline-none focus:border-[var(--gold)] placeholder:text-white/40 border-2 border-white/15" />
                        <button type="submit" disabled={loginLoading} data-testid="partner-submit"
                            className="gold-cta w-full h-14 rounded-2xl font-mono uppercase tracking-widest text-sm font-bold flex items-center justify-center gap-2">
                            {loginLoading ? <Loader2 className="animate-spin" size={16} /> : null}
                            Σύνδεση
                        </button>
                    </form>
                    <div className="mt-6 text-center">
                        <p className="text-[10px] font-mono text-white/40">Demo accounts (password: Store123!):</p>
                        <p className="text-[10px] font-mono text-white/60 mt-1">store@grillbros.gr · store@mamamia.gr · store@tokyobites.gr</p>
                    </div>
                    <button onClick={() => navigate("/")} className="block mx-auto mt-6 text-xs font-mono uppercase tracking-widest text-white/40 hover:text-white">
                        ← Πίσω στο app
                    </button>
                </div>
            </div>
        );
    }

    const filtered = orders.filter((o) => {
        if (filter === "active") return !["completed", "delivered", "rejected", "cancelled"].includes(o.status);
        if (filter === "history") return ["completed", "delivered", "rejected", "cancelled"].includes(o.status);
        return true;
    });

    // Group active orders by status for kanban-like layout
    const buckets = {
        pending:    filtered.filter(o => o.status === "pending"),
        accepted:   filtered.filter(o => o.status === "accepted"),
        preparing:  filtered.filter(o => o.status === "preparing"),
        ready:      filtered.filter(o => ["ready", "on_the_way"].includes(o.status)),
    };

    return (
        <div className="cyber-bg min-h-screen text-white" data-testid="partner-dashboard">
            {/* Header */}
            <header className="sticky top-0 z-40 glass-gold border-b border-[var(--gold)]/30 px-4 py-3 flex items-center gap-3">
                {storeData?.logo_url && <img src={storeData.logo_url} alt="logo" className="h-11 w-11 rounded-xl object-cover ring-2 ring-[var(--gold)]/40" />}
                <div className="flex-1 min-w-0">
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[var(--gold-light)]">PARTNER · {storeData?.cuisine?.toUpperCase() || ""}</p>
                    <h1 className="font-heading text-xl leading-none truncate" style={{ fontFamily: '"Cormorant Garamond", serif' }}>{storeData?.name || "Store"}</h1>
                </div>
                <button data-testid="sound-toggle" onClick={toggleSound} className="h-10 w-10 grid place-items-center rounded-xl bg-white/5 hover:bg-white/10 transition" title="Ήχος">
                    {soundOn ? <Volume2 size={16} className="text-[var(--gold-light)]" /> : <VolumeX size={16} className="text-white/40" />}
                </button>
                <button data-testid="analytics-link" onClick={() => navigate("/partner/analytics")} className="h-10 w-10 grid place-items-center rounded-xl bg-white/5 hover:bg-[var(--gold)]/20 transition" title="Στατιστικά">
                    <BarChart3 size={16} className="text-[var(--gold-light)]" />
                </button>
                <button data-testid="refresh-btn" onClick={fetchData} className="h-10 w-10 grid place-items-center rounded-xl bg-white/5 hover:bg-white/10 transition" title="Refresh">
                    <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
                </button>
                <button data-testid="logout-btn" onClick={logout} className="h-10 w-10 grid place-items-center rounded-xl bg-white/5 hover:bg-[var(--neon-red)]/20 transition" title="Logout">
                    <LogOut size={16} />
                </button>
            </header>

            {/* Filter tabs */}
            <div className="sticky top-[68px] z-30 glass-gold border-b border-white/10 px-4 py-3 flex gap-2">
                {[
                    { id: "active",  label: "ΕΝΕΡΓΕΣ",  count: orders.filter(o => !["completed", "delivered", "rejected", "cancelled"].includes(o.status)).length },
                    { id: "history", label: "ΙΣΤΟΡΙΚΟ", count: orders.filter(o => ["completed", "delivered", "rejected", "cancelled"].includes(o.status)).length },
                ].map((t) => (
                    <button key={t.id} data-testid={`filter-${t.id}`} onClick={() => setFilter(t.id)}
                        className={`pill-chip ${filter === t.id ? "active" : ""}`}>
                        {t.label} ({t.count})
                    </button>
                ))}
            </div>

            {/* Body — POS Kanban */}
            <div className="px-4 py-4 max-w-7xl mx-auto">
                {filtered.length === 0 ? (
                    <div className="text-center py-32">
                        <Bell size={32} className="mx-auto text-white/20 mb-3" />
                        <p className="font-mono text-xs uppercase tracking-widest text-white/40">// no_orders</p>
                        <p className="text-xs text-white/30 mt-1">Περιμένουμε την επόμενη παραγγελία...</p>
                    </div>
                ) : filter === "active" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { key: "pending",   title: "Νέες",         icon: Sparkles },
                            { key: "accepted",  title: "Αποδεκτές",    icon: Check },
                            { key: "preparing", title: "Ετοιμάζονται", icon: ChefHat },
                            { key: "ready",     title: "Έτοιμες",      icon: Package },
                        ].map((col) => {
                            const Icon = col.icon;
                            const list = buckets[col.key] || [];
                            return (
                                <div key={col.key} className="glass-gold rounded-2xl p-3 min-h-[200px]">
                                    <div className="flex items-center justify-between mb-3 px-1">
                                        <div className="flex items-center gap-2">
                                            <Icon size={14} className="text-[var(--gold-light)]" />
                                            <h3 className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-white/80">{col.title}</h3>
                                        </div>
                                        <span className="font-mono text-[var(--gold-light)] text-sm font-bold">{list.length}</span>
                                    </div>
                                    <div className="space-y-3">
                                        {list.length === 0 ? (
                                            <p className="text-[10px] text-white/25 text-center py-4 italic">—</p>
                                        ) : list.map((o) => (
                                            <PartnerOrderCard key={o.id} order={o} onUpdate={updateStatus} highlight={o.id === highlightId} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl mx-auto">
                        {filtered.map((o) => <PartnerOrderCard key={o.id} order={o} onUpdate={updateStatus} compact />)}
                    </div>
                )}
            </div>
        </div>
    );
};

const PartnerOrderCard = ({ order, onUpdate, highlight = false, compact = false }) => {
    const cfg = POS_STATUS[order.status] || POS_STATUS.pending;
    const created = new Date(order.created_at);
    const ago = Math.floor((Date.now() - created.getTime()) / 60000);
    const ageBucket = ago < 5 ? "fresh" : ago < 15 ? "ok" : "late";
    const ageColor = ageBucket === "fresh" ? "text-emerald-400" : ageBucket === "ok" ? "text-[var(--gold-light)]" : "text-[var(--neon-red)]";

    return (
        <article
            data-testid={`partner-order-${order.id}`}
            className={`relative rounded-2xl p-3 transition-all ${highlight ? "ring-2 ring-[var(--gold)] shadow-[0_0_30px_rgba(244,179,57,0.5)] animate-pulse" : ""}`}
            style={{ background: cfg.bg, border: `1.5px solid ${cfg.color}55` }}
        >
            {highlight && (
                <span className="absolute -top-2 -right-2 bg-[var(--gold)] text-[#0B0B0F] text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shadow-lg">
                    NEW
                </span>
            )}

            <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                    <p className="font-mono text-[9px] text-white/40">#{order.id.slice(0, 6).toUpperCase()}</p>
                    <h4 className="font-heading uppercase text-base leading-tight truncate">{order.customer_name}</h4>
                </div>
                <span className={`text-[9px] font-mono font-bold ${ageColor}`}>{ago < 1 ? "τώρα" : `${ago}'`}</span>
            </div>

            {/* Items */}
            <div className="bg-black/40 rounded-lg p-2.5 mb-2.5 text-sm space-y-1">
                {order.items.slice(0, compact ? 2 : 8).map((it, i) => (
                    <div key={i} className="flex justify-between">
                        <span className="text-white/85 truncate mr-2">
                            <span className="font-bold text-[var(--gold-light)]">{it.quantity}×</span> {it.name}
                        </span>
                    </div>
                ))}
                {compact && order.items.length > 2 && <p className="text-[10px] text-white/40">+ {order.items.length - 2} ακόμα</p>}
                <div className="border-t border-white/10 pt-1 flex justify-between font-bold">
                    <span className="text-xs text-white/60">Total</span>
                    <span className="font-mono text-[var(--gold-light)]">€{order.total.toFixed(2)}</span>
                </div>
            </div>

            {/* Customer info (only on active cards) */}
            {!compact && (
                <div className="space-y-1 text-[11px] mb-3">
                    <a href={`tel:${order.phone}`} className="flex items-center gap-1.5 text-white/70 hover:text-[var(--gold-light)]">
                        <Phone size={11} /><span className="font-mono">{order.phone}</span>
                    </a>
                    <div className="flex items-start gap-1.5 text-white/70">
                        <MapPin size={11} className="mt-0.5 shrink-0" /><span className="line-clamp-2">{order.address}</span>
                    </div>
                    {order.notes && (
                        <div className="bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-md px-2 py-1 mt-1">
                            <p className="text-[var(--gold-light)] italic">📝 {order.notes}</p>
                        </div>
                    )}
                    <p className="text-white/40 font-mono uppercase text-[9px]">
                        {order.payment_method === "card" ? "💳 Κάρτα" : "💵 Μετρητά"} · {order.payment_status === "paid" ? "Πληρωμένη" : "Pending"}
                    </p>
                </div>
            )}

            {/* Action buttons (POS-style large) */}
            {cfg.actions.length > 0 && !compact && (
                <div className="flex gap-2">
                    {cfg.actions.map((act) => {
                        const a = ACTIONS[act];
                        const Icon = a.icon;
                        const isReject = act === "reject";
                        return (
                            <button
                                key={act}
                                data-testid={`pos-${act}-${order.id}`}
                                onClick={() => onUpdate(order.id, a.to)}
                                className="flex-1 h-14 rounded-xl border-2 font-heading uppercase tracking-wider text-sm font-black flex items-center justify-center gap-2 transition active:scale-95 hover:bg-white/5"
                                style={{
                                    borderColor: a.color,
                                    color: a.color,
                                    background: isReject ? "transparent" : `${a.color}15`,
                                }}
                            >
                                <Icon size={18} strokeWidth={2.8} />
                                {a.label}
                            </button>
                        );
                    })}
                </div>
            )}

            {compact && (
                <span className="block text-[10px] font-mono uppercase tracking-widest text-center py-1.5 rounded-md"
                    style={{ color: cfg.color, background: cfg.bg }}>
                    {cfg.label}
                </span>
            )}
        </article>
    );
};
